import { ReactElement } from 'react';

import { Box, Flex, Grid, GridItem, Heading, useMediaQuery, useQuery, useToast } from '@chakra-ui/react';
import UfoDoodle from 'images/doodles/ufo.png';
import DoodleFallback from 'images/doodles/yellow-marker-big.png';
import { GetStaticPaths, GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Image from 'next/image';

import { StrapiImage } from '@/components/atoms';
import { ParsedHTMLText } from '@/components/functional';
import { DefaultLayout, Footer } from '@/components/layouts';
import { LetsBuildSomethingSection } from '@/components/sections/common';
import { CoverImage, LibraryRequestForm, LibraryRequestFormProps } from '@/components/sections/library';
import { SectionContentWrapper, SectionWrapper } from '@/components/wrappers';
import { PropsBuilder } from '@/graphql/api';
import {
  useCreateLibraryRequestMutation,
  useGetGlobalQuery,
  useGetLibrariesFilteredBySlugQuery,
  useGetLibrariesQuery,
  useGetMenuQuery,
} from '@/graphql/generated';
import { NextPageWithLayout } from '@/pages/_app';
import { DEFAULT_LOCALE, LOCALES, PAGE_REVALIDATE_TIME } from '@/utils/constants';
import { downloadFile } from '@/utils/helper-functions/downloadFile';

interface LibraryProps {
  preview: boolean;
  slug: string;
}

const Library: NextPageWithLayout<LibraryProps> = ({ slug }) => {
  const [isAboveSM] = useMediaQuery([useQuery({ above: 'sm' })]);
  const library = useGetLibrariesFilteredBySlugQuery({ slug }).data?.libraries?.data[0]!;

  const { title, content, cover, doodle } = library.attributes!;

  const toast = useToast({ duration: 5000 });

  const { mutateAsync: submitLibraryRequest } = useCreateLibraryRequestMutation();

  const onLibraryRequestSubmit: LibraryRequestFormProps['onSubmit'] = async (values) => {
    const response = await submitLibraryRequest({
      data: {
        captchaToken: values.captchaToken,
        email: values.email,
        marketingAgree: values.marketingAgree,
        name: values.name,
        library: library.id!,
      },
    });
    toast({
      status: 'success',
      title: 'Form successfully submitted 🍆',
    });
    const asset = response.createLibraryRequest?.data?.attributes?.library?.data?.attributes?.asset.data?.attributes!;

    downloadFile(asset.url);
  };

  const onLibraryRequestError = (_e?: Error) => {
    toast({
      status: 'error',
      title: "Couldn't submit form 😔",
    });
  };

  const renderDescription = (
    <Box
      color="purple.500"
      mt={{
        base: 28,
        md: 18,
      }}
    >
      <ParsedHTMLText content={content} innerStyles={{ strong: { fontWeight: 'semibold' } }} />
    </Box>
  );

  const renderCoverImage = (
    <>
      <Flex
        gridArea="cover"
        maxW={{ base: '190px', xs: '307px', md: '410px' }}
        justify={{ base: 'center', sm: 'flex-start' }}
      >
        <CoverImage cover={cover} />
      </Flex>
    </>
  );

  const renderDoodleImage = doodle?.data ? (
    <Box as={StrapiImage} image={doodle} />
  ) : (
    <Box
      as={Image}
      src={DoodleFallback}
      alt=""
      maxW={{
        base: '793px',
        sm: '864px',
        md: 'unset',
      }}
    />
  );

  const renderForm = (
    <Flex
      mt={{ base: 20, sm: 0 }}
      flexDir="column"
      alignItems="center"
      position="relative"
      pr={{
        sm: 14,
      }}
    >
      {!isAboveSM && (
        <Box position="absolute" top="-12%" zIndex={-1}>
          {renderDoodleImage}
        </Box>
      )}
      <LibraryRequestForm onSubmit={onLibraryRequestSubmit} onError={onLibraryRequestError} />
      <Box
        as={Image}
        src={UfoDoodle}
        alt=""
        w={{
          base: '100px',
          sm: '140px',
          md: '220px',
          lg: '260px',
        }}
        position={{
          base: 'initial',
          sm: 'absolute',
        }}
        transform={{
          base: 'none',
          sm: 'translateX(-50%)',
        }}
        bottom={{
          base: '-54%',
          md: '-120%',
        }}
        left="50%"
        mt={{
          base: 12,
          sm: 0,
        }}
      />
    </Flex>
  );

  return (
    <>
      <SectionWrapper>
        <SectionContentWrapper>
          {isAboveSM && (
            <Box
              position="absolute"
              zIndex={-1}
              top={{
                base: '25%',
                md: '18%',
                lg: '20%',
              }}
              left={{
                base: '-20%',
                md: '-40%',
                lg: '-30%',
                xl: '-20%',
              }}
            >
              {renderDoodleImage}
            </Box>
          )}
          <Heading
            as="h1"
            variant={{
              base: 'component-heading-large/xl',
              sm: 'component-heading-large/3xl',
              lg: 'component-heading-large/4xl',
            }}
            maxW="1062px"
            mb={{
              base: 20,
            }}
          >
            <ParsedHTMLText content={title} />
          </Heading>

          {isAboveSM ? (
            <Grid
              gridTemplateColumns={{
                base: '1fr',
                sm: 'repeat(2, 1fr)',
              }}
            >
              <GridItem maxW={{ sm: '410px' }} pb={{ base: '180px', sm: 0 }} position="relative">
                {renderForm}
              </GridItem>
              <GridItem>{renderCoverImage}</GridItem>
              <GridItem
                gridColumn={{
                  base: '1',
                  sm: '1 / span 2',
                  md: '2',
                }}
              >
                {renderDescription}
              </GridItem>
            </Grid>
          ) : (
            <Flex flexDir="column" alignItems="center">
              {renderCoverImage}
              {renderDescription}
              {renderForm}
            </Flex>
          )}
        </SectionContentWrapper>
      </SectionWrapper>

      <Box overflow="hidden">
        <LetsBuildSomethingSection />
      </Box>
    </>
  );
};

Library.getLayout = (page: ReactElement) => {
  const { metadata, preview, menu } = page.props;

  return (
    <DefaultLayout metadata={metadata} preview={preview} menu={menu}>
      {page}
      <Footer menu={menu} />
    </DefaultLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.ENV === 'staging') {
    return { paths: [], fallback: 'blocking' };
  }

  const libraries = (await useGetLibrariesQuery.fetcher()()).libraries?.data!;
  const paths = libraries.map((inventoryDefinition) => ({
    params: { slug: inventoryDefinition.attributes?.slug },
  }));

  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { params, locale, preview = false } = context;
  const slug = params?.slug as string;

  const propsBuilder = new PropsBuilder();

  const variables = {
    slug,
  };

  propsBuilder.withQuery(useGetGlobalQuery, {
    variables: {
      locale,
    },
  });
  propsBuilder.withQuery(useGetMenuQuery, {
    getMenu: (data) => data.menu,
  });
  const result = await propsBuilder.withQuery(useGetLibrariesFilteredBySlugQuery, {
    variables,
    isPrivate: preview,
    getMetadata: (data) => data.libraries?.data[0].attributes?.metadata,
  });

  const libraryItem = result.libraries?.data[0];

  const lang = await serverSideTranslations(locale || DEFAULT_LOCALE, [LOCALES.COMMON]);

  if (!libraryItem) {
    return {
      notFound: true,
      revalidate: PAGE_REVALIDATE_TIME,
    };
  }

  return {
    props: {
      ...(await propsBuilder.build(lang)),
      preview,
      slug,
    },
    revalidate: PAGE_REVALIDATE_TIME,
  };
};

export default Library;
